import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, SpinnerGap, User, X } from "@phosphor-icons/react";
import api from "../lib/api";
import { formatFollowers } from "../lib/formatters";
import { PlatformIcon } from "./PlatformIcon";
import { InfluencerAvatar } from "./InfluencerAvatar";

export function GlobalSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.searchInfluencers(q);
      setResults(res.data);
      setOpen(true);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
  };

  const handleSelect = (inf) => {
    setOpen(false);
    setQuery("");
    const path = inf.source === "subscriber" ? "/potential-influencers" : "/influencers";
    navigate(path, { state: { openInfluencerId: inf.id } });
  };

  const handleClear = () => {
    setQuery("");
    setResults([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Keyboard shortcut: Cmd/Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  return (
    <div className="relative" ref={containerRef} data-testid="global-search">
      <div className="relative flex items-center">
        <MagnifyingGlass size={16} className="absolute left-3 text-[#737373] pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search influencers..."
          value={query}
          onChange={handleChange}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          className="w-72 h-9 pl-9 pr-16 text-sm bg-zinc-50 border border-zinc-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#9FFF00] focus:border-[#9FFF00] placeholder:text-[#A3A3A3] transition-colors"
          data-testid="global-search-input"
        />
        {query && (
          <button onClick={handleClear} className="absolute right-10 text-[#737373] hover:text-[#0A0A0A]" data-testid="search-clear-btn">
            <X size={14} />
          </button>
        )}
        <span className="absolute right-3 text-[10px] text-[#A3A3A3] font-mono border border-zinc-200 px-1 py-0.5 rounded bg-white">
          {navigator.platform.includes("Mac") ? "⌘K" : "^K"}
        </span>
      </div>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-96 bg-white border border-zinc-200 rounded-md shadow-lg z-50 overflow-hidden" data-testid="search-results-dropdown">
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <SpinnerGap size={20} className="animate-spin text-[#9FFF00]" />
            </div>
          ) : results.length === 0 ? (
            <div className="py-6 text-center text-sm text-[#737373]">
              No influencers found for "{query}"
            </div>
          ) : (
            <div className="max-h-80 overflow-auto">
              {results.map((inf) => (
                <button
                  key={inf.id}
                  onClick={() => handleSelect(inf)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors text-left border-b border-zinc-50 last:border-0 cursor-pointer"
                  data-testid={`search-result-${inf.id}`}
                >
                  <InfluencerAvatar name={inf.customer_name} profiles={inf.social_profiles} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-[#0A0A0A] truncate">{inf.customer_name}</span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                        inf.influence_score >= 70 ? "bg-emerald-100 text-emerald-700" :
                        inf.influence_score >= 40 ? "bg-amber-100 text-amber-700" : "bg-zinc-100 text-zinc-600"
                      }`}>{inf.influence_score}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {inf.social_profiles?.slice(0, 3).map((p, i) => (
                        <div key={i} className="flex items-center gap-0.5">
                          <PlatformIcon platform={p.platform} size={12} />
                          <span className="text-[10px] text-[#737373]">{formatFollowers(p.followers)}</span>
                        </div>
                      ))}
                      <span className="text-[10px] text-[#A3A3A3] ml-auto">
                        {inf.source === "subscriber" ? "Potential" : "Customer"}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
